<template>
  <div
    v-for="announcement in visibleAnnouncements"
    :key="announcement.id"
    class="relative bg-gradient-to-r from-blue-700 to-blue-600 border-b border-blue-900/20 text-white flex"
    role="alert"
  >
    <div class="flex-1 flex items-center gap-2 px-4 py-2.5 border-r border-blue-900/20">
      <s-icon name="information-outline" size="20" class="text-blue-100 drop-shadow" />
      <div class="flex-1 text-sm font-medium leading-tight drop-shadow">
        {{ getLocalizedContent(announcement.content) }}
      </div>
    </div>
    <button
      v-if="announcement.dismissable"
      @click="dismiss(announcement.id)"
      class="flex-shrink-0 px-4 py-2.5 hover:bg-blue-700 hover:text-blue-100 border-l border-blue-500/70 transition"
      :aria-label="t('dismissAnnouncement')"
    >
      <s-icon name="close" size="20" class="drop-shadow" />
    </button>
  </div>
</template>

<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {usePage} from '@inertiajs/vue3';
import {useLocalStorage} from '@vueuse/core';
import SIcon from '../design-system/SIcon.vue';
import {useI18n} from 'vue-i18n';
import LocalizedContentDto = App.DTOs.LocalizedContentDto;

const {t, locale} = useI18n({
  inheritLocale: true,
  scope: 'local',
});

const page = usePage();

// Store announcements locally so they persist across Inertia navigation
// (announcements are only sent on initial page load, not on subsequent Inertia requests)
const storedAnnouncements = ref<any[]>([]);

// Watch for announcements from the server and update stored announcements
watch(
  () => page.props.systemAnnouncements,
  (newAnnouncements) => {
    if (newAnnouncements) {
      storedAnnouncements.value = newAnnouncements;
    }
  },
  {immediate: true}
);

const announcements = computed(() => storedAnnouncements.value);

const dismissedIds = useLocalStorage<number[]>('dismissed-announcements', []);

function dismiss(id: number): void {
  if (!dismissedIds.value.includes(id)) {
    dismissedIds.value = [...dismissedIds.value, id];
  }
}

const visibleAnnouncements = computed(() => {
  return announcements.value.filter(
    (announcement) => !announcement.dismissable || !dismissedIds.value.includes(announcement.id)
  );
});

function getLocalizedContent(content: LocalizedContentDto): string {
  return content[locale.value as 'en' | 'fr'] || content.en;
}
</script>

<i18n>
{
  "en": {
    "dismissAnnouncement": "Dismiss announcement"
  },
  "fr": {
    "dismissAnnouncement": "Fermer l'annonce"
  }
}
</i18n>
